import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import HeaderOne from "../components/HeaderOne";
import Breadcrumb from "../components/Breadcrumb";
import ShippingOne from "../components/ShippingOne";
import FooterOne from "../components/FooterOne";
import BottomFooter from "../components/BottomFooter";
import ScrollToTop from "react-scroll-to-top";
import ColorInit from "../helper/ColorInit";
import productService from "../services/productService";
import { useCart } from "../contexts/CartContext";
import QuantityControl from "../helper/QuantityControl";

const CustomPackagePage = () => {
  const [products, setProducts] = useState([]);
  const [quantities, setQuantities] = useState({});
  const navigate = useNavigate();
  const { addCustomPackageToCart } = useCart();

  // Add fallback logic for image API URL
  const imageApiUrl = (process.env.REACT_APP_IMAGE_API_URL || process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/,'');

  useEffect(() => {
    const loadProducts = async () => {
      const response = await productService.getProducts({ 
        pageSize: 1000,
        isAvailableForCustomPackage: true
      });
      if (response.succeeded && response.products) {
        setProducts(response.products);
        const initialQuantities = {};
        response.products.forEach((p) => {
          initialQuantities[p.id] = 0;
        });
        setQuantities(initialQuantities);
      }
    };
    loadProducts();
  }, []);

  const handleQuantityChange = (productId, value) => {
    setQuantities({
      ...quantities,
      [productId]: Math.max(0, Number(value))
    });
  };

  const handleAddToCart = async () => {
    // Build custom package payload
    const items = products.filter(p => quantities[p.id] > 0).map(p => ({
      productId: p.id,
      quantity: quantities[p.id],
      weightGrams: 250,
      unitPrice: (p.discountedPrice ?? p.price) / 4
    }));

    // Check if no items selected
    if (items.length === 0) {
      alert("Lütfen en az bir ürün seçin!");
      return;
    }

    // Calculate total weight in grams
    const totalWeightGrams = items.reduce((sum, item) => sum + (item.quantity * item.weightGrams), 0);
    
    // Check minimum 1kg (1000g) requirement
    if (totalWeightGrams < 1000) {
      alert("Özelleştirilmiş paket minimum 1kg olmalıdır. Lütfen daha fazla ürün ekleyin.");
      return;
    }

    try {
      const response = await addCustomPackageToCart(items, products);
      if (response.success) {
        navigate("/cart");
      } else {
        console.error("Custom package add failed:", response.message);
        alert(response.message || "Sepete eklenirken bir hata oluştu. Lütfen tekrar deneyin.");
      }
    } catch (err) {
      console.error("Custom package error:", err);
      alert("Sepete eklenirken bir hata oluştu. Lütfen tekrar deneyin.");
    }
  };

  // Calculate total amount for selected packages
  const totalAmount = products.reduce((sum, product) => {
    const qty = quantities[product.id] || 0;
    const unitPrice = (product.discountedPrice ?? product.price) / 4;
    return sum + unitPrice * qty;
  }, 0);

  // List of selected products for summary
  const selectedProducts = products.filter(p => quantities[p.id] > 0);
  
  // Calculate total weight for selected products
  const totalWeightGrams = selectedProducts.reduce((sum, p) => sum + (quantities[p.id] * 250), 0);
  
  // Helper to format weight display
  const formatWeight = (qty) => {
    const grams = qty * 250;
    if (grams >= 1000) {
      return `${(grams / 1000).toFixed(2)} kg`;
    }
    return `${grams} g`;
  };

  // Helper to format total weight
  const formatTotalWeight = (grams) => {
    if (grams >= 1000) {
      return `${(grams / 1000).toFixed(2)} kg`;
    }
    return `${grams} g`;
  };

  return (
    <>
      <ColorInit color={false} />
      <ScrollToTop smooth color="#299E60" />
      <HeaderOne />
      <Breadcrumb title={"Kendi Paketini Oluştur"} />
      <section className="shop py-80">
        <div className="container container-lg">
          <div className="row">
            <div className="col-lg-9">
              <div className="list-grid-wrapper list-view">
                {products.map((product) => (
                  <div
                    key={product.id}
                    className="product-card h-100 p-16 border border-gray-100 hover-border-main-600 rounded-16 mb-16"
                  >
                    <div className="d-flex align-items-center w-100">
                      <Link
                        to={`/product-details/${product.slug || product.id}`}
                        className="product-card__thumb flex-center rounded-8 bg-gray-50 position-relative"
                      >
                        <img
                          src={
                            product.imageUrl?.startsWith('http')
                              ? product.imageUrl
                              : `${imageApiUrl}${product.imageUrl}`
                          }
                          alt={product.name}
                          className="max-w-unset"
                        />
                      </Link>
                      <div className="product-card__content ms-16 flex-grow-1">
                        <h6 className="title text-lg fw-semibold mb-4">
                          {product.name} (250g)
                        </h6>
                        <div className="product-card__price mb-16">
                          <span className="text-heading text-md fw-semibold">
                            ₺{((product.discountedPrice ?? product.price) / 4).toFixed(2)}
                          </span>
                          <span className="text-gray-500 fw-normal">/250g</span>
                        </div>
                        <div className="quantity-control-wrapper">
                          <QuantityControl
                            initialQuantity={quantities[product.id]}
                            onQuantityChange={(newQty) => handleQuantityChange(product.id, newQty)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-lg-3">
              <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                <h6 className="text-xl mb-24">Sipariş Özeti</h6>
                {selectedProducts.length > 0 ? (
                  <>
                    <ul className="mb-16">
                      {selectedProducts.map((p) => (
                        <li key={p.id} className="d-flex justify-content-between mb-8">
                          <span>{p.name}</span>
                          <span>{formatWeight(quantities[p.id])}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="d-flex justify-content-between mb-16 border-top pt-16">
                      <span className="fw-semibold">Toplam Ağırlık:</span>
                      <span className={`fw-semibold ${totalWeightGrams < 1000 ? 'text-danger' : 'text-success'}`}>
                        {formatTotalWeight(totalWeightGrams)}
                      </span>
                    </div>
                    {totalWeightGrams < 1000 && (
                      <div className="alert alert-warning p-12 mb-16 text-sm">
                        Minimum 1kg gereklidir. {formatTotalWeight(1000 - totalWeightGrams)} daha ekleyin.
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-gray-500 text-sm mb-16">Henüz seçim yapmadınız.</p>
                )}
                <div className="text-heading text-2xl fw-semibold mb-16">
                  ₺{totalAmount.toFixed(2)}
                </div>
                <button 
                  type="button" 
                  className={`btn w-100 ${selectedProducts.length > 0 && totalWeightGrams >= 1000 ? 'btn-main' : 'btn-secondary'}`}
                  onClick={handleAddToCart}
                  disabled={selectedProducts.length === 0 || totalWeightGrams < 1000}
                >
                  Sepete Ekle
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      <ShippingOne />
      <FooterOne />
      <BottomFooter />
    </>
  );
};

export default CustomPackagePage; 